import { useSelector, useDispatch } from 'react-redux'
import { useCallback } from 'react'
import {
  selectSearchState,
  selectSearchFilters,
  selectSearchResults,
  selectSearchLoading,
  selectSearchError,
  selectSearchPagination,
  selectSearchHistory,
  updateFilter,
  setSearchFilters,
  clearError,
  resetSearch
} from '../utils/searchReducer'
import {
  selectBookingState,
  selectSelectedDoctor,
  selectShowAvailabilityView,
  selectSelectedSlot,
  selectAvailabilityData,
  selectCurrentView,
  selectBookingLoading,
  selectBookingError,
  selectBookingSuccess,
  selectBookingHistory,
  openAvailabilityView,
  closeAvailabilityView,
  setSelectedReason,
  setAgreedToTerms,
  navigateWeek,
  selectSlot,
  backToCalendar,
  clearBookingError,
  resetBookingState
} from '../utils/bookingReducer'
import {
  performSearch,
  changePage,
  fetchAvailability,
  confirmBooking,
  openAvailabilityViewAndFetch,
  searchWithFilters
} from '../utils/thunks'

// Search hook - equivalent to useSearchStore from Zustand
export const useSearchRedux = () => {
  const dispatch = useDispatch()

  // Selectors
  const searchFilters = useSelector(selectSearchFilters)
  const searchResults = useSelector(selectSearchResults)
  const loading = useSelector(selectSearchLoading)
  const error = useSelector(selectSearchError)
  const pagination = useSelector(selectSearchPagination)
  const searchHistory = useSelector(selectSearchHistory)

  // Actions
  const performSearchAction = useCallback(
    (filters) => {
      return dispatch(performSearch(filters))
    },
    [dispatch]
  )

  const changePageAction = useCallback(
    (page) => {
      return dispatch(changePage(page))
    },
    [dispatch]
  )

  const updateFilterAction = useCallback(
    (filterType, value) => {
      dispatch(updateFilter({ filterType, value }))
    },
    [dispatch]
  )

  const setSearchFiltersAction = useCallback(
    (filters) => {
      dispatch(setSearchFilters(filters))
    },
    [dispatch]
  )

  const clearErrorAction = useCallback(() => {
    dispatch(clearError())
  }, [dispatch])

  const resetSearchAction = useCallback(() => {
    dispatch(resetSearch())
  }, [dispatch])

  const searchWithFiltersAction = useCallback(
    (newFilters) => {
      return dispatch(searchWithFilters(newFilters))
    },
    [dispatch]
  )

  return {
    // State
    searchFilters,
    searchResults,
    loading,
    error,
    pagination,
    searchHistory,

    // Actions
    performSearch: performSearchAction,
    changePage: changePageAction,
    updateFilter: updateFilterAction,
    setSearchFilters: setSearchFiltersAction,
    clearError: clearErrorAction,
    resetSearch: resetSearchAction,
    searchWithFilters: searchWithFiltersAction
  }
}

// Booking hook - equivalent to useBookingStore from Zustand
export const useBookingRedux = () => {
  const dispatch = useDispatch()

  // Selectors
  const selectedDoctor = useSelector(selectSelectedDoctor)
  const showAvailabilityView = useSelector(selectShowAvailabilityView)
  const selectedSlot = useSelector(selectSelectedSlot)
  const availabilityData = useSelector(selectAvailabilityData)
  const currentView = useSelector(selectCurrentView)
  const bookingLoading = useSelector(selectBookingLoading)
  const bookingError = useSelector(selectBookingError)
  const bookingSuccess = useSelector(selectBookingSuccess)
  const bookingHistory = useSelector(selectBookingHistory)
  const bookingState = useSelector(selectBookingState)

  // Actions
  const openAvailabilityViewAction = useCallback(
    (doctor, preSelectedSlot = null) => {
      dispatch(openAvailabilityView({ doctor, preSelectedSlot }))
    },
    [dispatch]
  )

  const closeAvailabilityViewAction = useCallback(() => {
    dispatch(closeAvailabilityView())
  }, [dispatch])

  const fetchAvailabilityAction = useCallback(() => {
    return dispatch(fetchAvailability())
  }, [dispatch])

  const confirmBookingAction = useCallback(() => {
    return dispatch(confirmBooking())
  }, [dispatch])

  const openAvailabilityViewAndFetchAction = useCallback(
    (doctor) => {
      return dispatch(openAvailabilityViewAndFetch(doctor))
    },
    [dispatch]
  )

  const setSelectedReasonAction = useCallback(
    (reason) => {
      dispatch(setSelectedReason(reason))
    },
    [dispatch]
  )

  const setAgreedToTermsAction = useCallback(
    (agreed) => {
      dispatch(setAgreedToTerms(agreed))
    },
    [dispatch]
  )

  const navigateWeekAction = useCallback(
    (direction) => {
      dispatch(navigateWeek(direction))
    },
    [dispatch]
  )

  const selectSlotAction = useCallback(
    (slot) => {
      dispatch(selectSlot(slot))
    },
    [dispatch]
  )

  const backToCalendarAction = useCallback(() => {
    dispatch(backToCalendar())
  }, [dispatch])

  const clearBookingErrorAction = useCallback(() => {
    dispatch(clearBookingError())
  }, [dispatch])

  const resetBookingStateAction = useCallback(() => {
    dispatch(resetBookingState())
  }, [dispatch])

  // Computed values
  const canNavigateBack = useCallback(() => {
    const { currentWeekStart } = bookingState
    const today = new Date()
    const getMonday = (date) => {
      const d = new Date(date)
      const day = d.getDay()
      const diff = d.getDate() - day + (day === 0 ? -6 : 1)
      return new Date(d.setDate(diff))
    }
    const currentWeekMonday = getMonday(today)
    const previousWeek = new Date(currentWeekStart)
    previousWeek.setDate(previousWeek.getDate() - 7)

    return previousWeek >= currentWeekMonday
  }, [bookingState])

  return {
    // State
    selectedDoctor,
    showAvailabilityView,
    selectedSlot,
    availabilityData,
    currentView,
    bookingLoading,
    bookingError,
    bookingSuccess,
    bookingHistory,
    currentWeekStart: bookingState.currentWeekStart,
    hasInitializedWeek: bookingState.hasInitializedWeek,
    selectedReason: bookingState.selectedReason,
    agreedToTerms: bookingState.agreedToTerms,
    showReasonModal: bookingState.showReasonModal,

    // Actions
    openAvailabilityView: openAvailabilityViewAction,
    closeAvailabilityView: closeAvailabilityViewAction,
    fetchAvailability: fetchAvailabilityAction,
    confirmBooking: confirmBookingAction,
    openAvailabilityViewAndFetch: openAvailabilityViewAndFetchAction,
    setSelectedReason: setSelectedReasonAction,
    setAgreedToTerms: setAgreedToTermsAction,
    navigateWeek: navigateWeekAction,
    selectSlot: selectSlotAction,
    backToCalendar: backToCalendarAction,
    clearBookingError: clearBookingErrorAction,
    resetBookingState: resetBookingStateAction,

    // Computed values
    canNavigateBack
  }
}

// Combined hook for components that need both search and booking
export const useSearchEngine = () => {
  const search = useSearchRedux()
  const booking = useBookingRedux()

  return {
    search,
    booking
  }
}

// Simplified search hook (matches the original useSearch from providers)
export const useSearch = () => {
  const {
    searchFilters,
    searchResults,
    loading,
    error,
    pagination,
    performSearch,
    updateFilter,
    changePage,
    resetSearch
  } = useSearchRedux()

  return {
    filters: searchFilters,
    results: searchResults,
    loading,
    error,
    pagination,
    performSearch,
    updateFilter,
    changePage,
    resetSearch
  }
}

// Simplified booking hook (matches the original useBooking from providers)
export const useBooking = () => {
  const {
    selectedDoctor,
    showAvailabilityView,
    availabilityData,
    selectedSlot,
    currentView,
    bookingLoading,
    bookingError,
    bookingSuccess,
    openAvailabilityView,
    closeAvailabilityView,
    fetchAvailability,
    selectSlot,
    confirmBooking
  } = useBookingRedux()

  return {
    selectedDoctor,
    showAvailabilityView,
    availabilityData,
    selectedSlot,
    currentView,
    bookingLoading,
    bookingError,
    bookingSuccess,
    openAvailabilityView,
    closeAvailabilityView,
    fetchAvailability,
    selectSlot,
    confirmBooking
  }
}

// History hook for accessing search and booking history
export const useHistory = () => {
  const { searchHistory } = useSearchRedux()
  const { bookingHistory } = useBookingRedux()

  const getRecentActivity = useCallback(() => {
    const activity = [
      ...searchHistory.map((search) => ({
        type: 'search',
        timestamp: search.timestamp,
        data: search
      })),
      ...bookingHistory.map((booking) => ({
        type: 'booking',
        timestamp: booking.bookingDate,
        data: booking
      }))
    ]

    return activity.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
  }, [searchHistory, bookingHistory])

  return {
    searches: searchHistory,
    bookings: bookingHistory,
    getRecentActivity
  }
}
